import { useEffect, useRef, useState } from 'react';
import { turnstileSiteKey } from '../../env.json';

/**
 * Owns the single Cloudflare Turnstile widget used by the booking flow.
 *
 * Why this exists (see REPORTS/TURNSTILE.pdf): ~15% of challenges issued during
 * the booking-open rush were never solved, and previously nothing recovered a
 * stalled widget — it simply spun on "Verifying..." forever, which members read
 * as Cloudflare blocking their booking. This hook:
 *   - can be enabled *before* the booking window opens (pre-warm), so a token is
 *     usually ready by the time the captcha modal appears;
 *   - renders in `interaction-only` mode, so members only see Cloudflare's box
 *     when it genuinely needs a click (progress is shown by TurnstileBadge);
 *   - runs a stall watchdog that silently resets the widget a few times before
 *     surfacing a clear "failed" state with a manual retry;
 *   - reports every lifecycle event to Microsoft Clarity for diagnosis.
 *
 * Tokens are single-use server-side (Cloudflare siteverify), so callers must
 * call `consume()` after every request that carried one.
 *
 * status: 'idle' | 'loading' | 'verifying' | 'interactive' | 'ready'
 *       | 'retrying' | 'failed' | 'unsupported'
 */

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
const SCRIPT_WAIT_MS = 20000;  // how long to wait for api.js before declaring failure
const POLL_MS = 200;
const STALL_MS = 8000;         // no token within this long (and not interactive) => stalled
const MAX_AUTO_RESETS = 3;     // silent resets before showing the failed state

export const trackTurnstile = (event, code) => {
  try {
    if (typeof window.clarity === 'function') {
      window.clarity('event', `turnstile_${event}`);
      if (code !== undefined && code !== null) {
        window.clarity('set', 'turnstile_error', String(code));
      }
    }
  } catch (err) {
    // telemetry must never affect the booking flow
  }
};

const useTurnstile = ({ enabled }) => {
  const [token, setTokenState] = useState('');
  const [status, setStatusState] = useState('idle');

  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const tokenRef = useRef('');
  const statusRef = useRef('idle');
  const enabledRef = useRef(enabled);
  const mountedRef = useRef(true);
  const stallTimerRef = useRef(null);
  const pollTimerRef = useRef(null);
  const autoResetsRef = useRef(0);

  // All widget callbacks are registered once at render time, so everything they
  // touch goes through refs (or stable state setters) to avoid stale closures.
  const setToken = (value) => {
    tokenRef.current = value;
    if (mountedRef.current) setTokenState(value);
  };
  const setStatus = (value) => {
    statusRef.current = value;
    if (mountedRef.current) setStatusState(value);
  };

  const clearStall = () => {
    if (stallTimerRef.current) clearTimeout(stallTimerRef.current);
    stallTimerRef.current = null;
  };

  const clearPoll = () => {
    if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    pollTimerRef.current = null;
  };

  const resetWidget = (nextStatus) => {
    setToken('');
    setStatus(nextStatus);
    try {
      if (window.turnstile && widgetIdRef.current) {
        window.turnstile.reset(widgetIdRef.current);
      }
    } catch (err) {
      trackTurnstile('reset_error', err.message);
    }
    armStall();
  };

  // The single recovery mechanism. Cloudflare's own `retry: 'auto'` handles
  // transient errors; this only steps in if that still hasn't produced a token.
  const armStall = () => {
    clearStall();
    stallTimerRef.current = setTimeout(() => {
      stallTimerRef.current = null;
      if (!enabledRef.current || !widgetIdRef.current) return;
      if (tokenRef.current || statusRef.current === 'interactive') return;

      if (autoResetsRef.current < MAX_AUTO_RESETS) {
        autoResetsRef.current += 1;
        trackTurnstile('stall_reset', autoResetsRef.current);
        resetWidget('retrying');
      } else {
        trackTurnstile('failed');
        setStatus('failed');
      }
    }, STALL_MS);
  };

  const renderWidget = () => {
    if (!window.turnstile || !containerRef.current) return false;
    if (widgetIdRef.current) return true;

    try {
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: turnstileSiteKey,
        // Hidden unless Cloudflare needs a tick; progress is shown instead by
        // the TurnstileBadge pill (bottom-right, with the Cloudflare logo).
        appearance: 'interaction-only',
        theme: 'light',
        retry: 'auto',
        'retry-interval': 1500,
        'refresh-expired': 'auto',
        'refresh-timeout': 'auto',
        callback: (newToken) => {
          clearStall();
          autoResetsRef.current = 0;
          setToken(newToken);
          setStatus('ready');
          trackTurnstile('ready');
        },
        'error-callback': (code) => {
          trackTurnstile('error', code);
          setToken('');
          if (statusRef.current !== 'failed') setStatus('retrying');
          if (!stallTimerRef.current) armStall();
          return true; // handled — suppresses Cloudflare's console error/throw
        },
        'expired-callback': () => {
          trackTurnstile('expired');
          setToken('');
          setStatus('verifying');
          armStall();
        },
        'timeout-callback': () => {
          trackTurnstile('timeout');
          setToken('');
          setStatus('verifying');
          armStall();
        },
        'before-interactive-callback': () => {
          trackTurnstile('interactive');
          clearStall(); // give the member time to tick the box
          setStatus('interactive');
        },
        'after-interactive-callback': () => {
          if (!tokenRef.current) {
            setStatus('verifying');
            armStall();
          }
        },
        'unsupported-callback': () => {
          trackTurnstile('unsupported');
          clearStall();
          setStatus('unsupported');
        },
      });
    } catch (err) {
      widgetIdRef.current = null;
      trackTurnstile('render_error', err.message);
      setStatus('failed');
      return true; // stop polling; the member can retry manually
    }

    trackTurnstile('render');
    setStatus('verifying');
    armStall();
    return true;
  };

  const start = () => {
    clearPoll();
    if (renderWidget()) return;

    setStatus('loading');
    const startedAt = Date.now();
    pollTimerRef.current = setInterval(() => {
      if (!enabledRef.current) {
        clearPoll();
        return;
      }
      if (renderWidget()) {
        clearPoll();
      } else if (Date.now() - startedAt > SCRIPT_WAIT_MS) {
        clearPoll();
        trackTurnstile('script_timeout');
        setStatus('failed');
      }
    }, POLL_MS);
  };

  const teardown = () => {
    clearPoll();
    clearStall();
    // Best-effort: the container may already be detached during unmount.
    try {
      if (window.turnstile && widgetIdRef.current) {
        window.turnstile.remove(widgetIdRef.current);
      }
    } catch (err) {
      console.warn('Turnstile widget cleanup skipped:', err.message);
    }
    widgetIdRef.current = null;
    autoResetsRef.current = 0;
    setToken('');
    setStatus('idle');
  };

  // api.js failed to load (network blip, blocked request): drop the dead tag
  // and add a fresh one so a manual retry can actually recover.
  const reloadScript = () => {
    try {
      document
        .querySelectorAll(`script[src^="${SCRIPT_SRC}"]`)
        .forEach((el) => el.parentNode && el.parentNode.removeChild(el));
      const script = document.createElement('script');
      script.src = SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      document.body.appendChild(script);
    } catch (err) {
      trackTurnstile('script_reload_error', err.message);
    }
  };

  const retry = () => {
    if (!enabledRef.current) return;
    trackTurnstile('manual_retry');
    autoResetsRef.current = 0;
    if (window.turnstile && widgetIdRef.current) {
      resetWidget('verifying');
      return;
    }
    if (!window.turnstile) reloadScript();
    start();
  };

  // Call after every request that carried the token — it is single-use.
  const consume = () => {
    if (!enabledRef.current || !widgetIdRef.current) {
      setToken('');
      return;
    }
    autoResetsRef.current = 0;
    resetWidget('verifying');
  };

  // Declared before the enable effect on purpose: React runs cleanups in
  // declaration order, so on unmount this flips first and teardown() below
  // skips setState on the unmounting component.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    enabledRef.current = enabled;
    if (!enabled) return undefined;
    start();
    return () => {
      enabledRef.current = false;
      teardown();
    };
    // eslint-disable-next-line
  }, [enabled]);

  return { containerRef, token, status, retry, consume };
};

export default useTurnstile;
